import DOMPurify from 'dompurify';
import { marked } from 'marked';
import chatElements from './chatElements';
import { scheduleQuestions } from '../flows/flowScheduleCall';
import { newsletterQuestions } from '../flows/flowJoinNewsletter';
import { submitToGravityForm, updateTranscriptInDB } from './chatFormHandling';
import { 
    getCurrentFlow,
    getCurrentQuestionIndex,
    incrementQuestionIndex,
    getUserResponses,
    getUserResponse,
    setUserResponse,
    setUserResponses,
    getIsResuming,
    setIsResuming
} from './chatState';

const {
  chatBody,
  chatInput,
  chatSend,
} = chatElements;

let transcriptState = {
    transcript: [],
    contact_info: {},
    started_schedule: false,
    finished_schedule: false,
    started_lead_form: false,
    finished_lead_form: false,
    started_newsletter: false,
    finished_newsletter: false
};

// Adds message to chatbot
function addMessage(content, sender, delay = 0) {
    content = content ? String(content) : "";

    chatInput.disabled = true;

    if (sender === 'bot') {
        setTimeout(() => {
            chatInput.disabled = false;
            chatInput.focus();
        }, 500);
    }

    setTimeout(() => {
        const message = document.createElement('div');
        message.className = `chat-message ${sender}`;

        if (sender === 'bot') {
            message.innerHTML = DOMPurify.sanitize(marked.parse(content));
        } else {
            message.textContent = content;
        }

        chatBody.appendChild(message);
        chatInput.disabled = false;
        chatBody.scrollTop = chatBody.scrollHeight;

        // Add to transcript and update DB
        transcriptState.transcript.push({ sender, content });
        updateTranscriptInDB(transcriptState);
    }, delay);
}

// Handles messaging for each flow
function chatbotFlowHandler(userMessage) {
    const flow = getCurrentFlow();

    if (flow === 'schedule') {
        handleScheduleMessage(flow, userMessage, scheduleQuestions);
    } else if (flow === 'askme') {
        handleAskMeMessage(flow, userMessage);
    } else if (flow === 'newsletter') {
        handleScheduleMessage(flow, userMessage, newsletterQuestions);
    }
}

function handleSendMessage() {
    const userMessage = chatInput.value.trim();
    if (!userMessage) return;

    // Pass input to the appropriate flow
    chatbotFlowHandler(userMessage);

    // Clear input field
    chatInput.value = '';
}

// Listen for input and submit messages
chatSend.addEventListener('click', handleSendMessage);
chatInput.addEventListener('keypress', (event) => {
    if (event.key === 'Enter') {
        handleSendMessage();
    }
});

const whatsNextButtons = [
    { key: 'schedule', button: "Schedule a call with a benefits advisor" },
    // { key: 'askme', button: `Ask questions about ${programAcronym} benefits` },
    { key: 'getstarted', button: `Get started with a quote`},
    // { key: 'support', button: "Email your support team" },
    { key: 'newsletter', button: "Sign up for Decisely's business newsletter" },
];

function whatsNextHelp(flow = null) {
    addMessage('Is there something else I can help you with?', 'bot', 500);
    addMessage('I\'d like to...', 'user', 500);
    setTimeout(() => {
        chatInput.disabled = true;
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'whats-next-buttons';

        const filteredButtons = whatsNextButtons.filter(btn => btn.key !== flow);

        filteredButtons.forEach(btn => {
            const button = document.createElement('button');
            button.textContent = btn.button;
            button.className = 'chat-option-button';
            button.dataset.option = btn.key;

            button.addEventListener('click', () => {
                buttonContainer.classList.add("disabled");
                const chatButtons = document.querySelectorAll('.chat-option-button');
                chatButtons.forEach(btnEl => btnEl.disabled = true );
                button.classList.add("selected");
                startChat(btn.key, false);
            });

            buttonContainer.appendChild(button);
        });

        chatBody.appendChild(buttonContainer);
        chatBody.scrollTop = chatBody.scrollHeight;
    }, 1000);
}

// Asks questions for schedule and newsletter options
function askNextQuestion(flow = '') {
    const questions = flow === 'newsletter' ? newsletterQuestions : scheduleQuestions;
    const currentIndex = getCurrentQuestionIndex();

    if (currentIndex < questions.length) {
        const currentQ = questions[currentIndex];

        // If resuming, show the alternate question for only the first follow-up
        const questionText = getIsResuming()
            ? currentQ.if_first || currentQ.question
            : currentQ.question;

        // Turn off resume mode after using it once
        if (getIsResuming()) {
            addMessage("Great! I just need a little more info to wrap this up.", 'bot');
            setIsResuming(false);
        }

        addMessage(questionText, 'bot');
    } else {
        submitToGravityForm(flow);
    }
}

function handleScheduleMessage(currentFlow, userMessage, questions) {
    const currentQ = questions[getCurrentQuestionIndex()];

    // Validate input if required
    if (currentQ.validate && !currentQ.validate(userMessage)) {
        addMessage(`Oops! That doesn't look like a valid ${currentQ.errorMessage}. Please try again.`, 'bot');
        return;
    }

    setUserResponse('thread_id', (sessionStorage.getItem('decisely_chat_thread_id') ?? ''));
    setUserResponse(currentQ.key, userMessage);
    setUserResponses(getUserResponses());

    // Display user message
    addMessage(userMessage, 'user');

    // Move to the next question or submit form
    incrementQuestionIndex();
    setTimeout(() => askNextQuestion(currentFlow), 1500);
}

export { 
    addMessage,
    chatbotFlowHandler,
    handleSendMessage,
    whatsNextHelp,
    askNextQuestion,
    handleScheduleMessage
};
